import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFormat } from '../../context/FormatContext';
import { useData } from '../../context/DataContext';

const {
  FiMonitor, FiSmartphone, FiBook, FiDownload, FiCheckCircle,
  FiStar, FiPrinter, FiRefreshCw, FiEye, FiLoader, FiHome,
  FiLock,
} = FiIcons;

const STYLE_PRESETS = [
  { id: 'Classic Novel', icon: FiBook, desc: 'Traditional serif fonts with standard paragraph indents. Perfect for fiction.' },
  { id: 'Modern Nonfiction', icon: FiMonitor, desc: 'Clean sans-serif headings with block paragraphs. Ideal for resources.' },
  { id: 'Devotional', icon: FiSmartphone, desc: 'Elegant typography with decorative scene breaks for daily readings.' },
];

const STYLE_RECOMMENDATIONS = {
  'Classic Novel': ['straight_line', 'diamond', 'star'],
  'Modern Nonfiction': ['rectangle', 'straight_line', 'gradient_line'],
  'Devotional': ['floral_heart', 'star', 'rotated_floral_heart'],
};

const GradientLinePreview = () => (
  <span className="block h-[2px] rounded-full" style={{
    background: 'linear-gradient(to right,transparent,#5eead4 20%,#0d9488 50%,#5eead4 80%,transparent)',
    width: '100%', maxWidth: '110px', margin: '0 auto',
  }} />
);

const ALL_ORNAMENTS = [
  { id: 'star', label: 'Star', symbol: '✦', preview: '✦ * ✦ * ✦', description: 'Classic four-pointed star.', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-300', activeBg: 'bg-amber-100' },
  { id: 'diamond', label: 'Diamond', symbol: '◆', preview: '◆ * ◆ * ◆', description: 'Bold solid diamond.', color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-300', activeBg: 'bg-rose-100' },
  { id: 'snowflake', label: 'Snowflake', symbol: '❄', preview: '— ❄ ❄ ❄ —', description: 'Delicate snowflake.', color: 'text-sky-700', bg: 'bg-sky-50', border: 'border-sky-300', activeBg: 'bg-sky-100' },
  { id: 'rectangle', label: 'Rectangle', symbol: '▮', preview: '▮ ▮ ▮', description: 'Minimal geometric blocks.', color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-300', activeBg: 'bg-slate-100' },
  { id: 'straight_line', label: 'Straight Line', symbol: '—', preview: '——————————', description: 'Simple em-dash rule.', color: 'text-stone-600', bg: 'bg-stone-50', border: 'border-stone-300', activeBg: 'bg-stone-100' },
  { id: 'shell', label: 'Shell', symbol: '🐚', preview: '· 🐚 🐚 🐚 ·', description: 'Organic shell motif.', color: 'text-yellow-800', bg: 'bg-yellow-50', border: 'border-yellow-300', activeBg: 'bg-yellow-100' },
  { id: 'gradient_line', label: 'Gradient Line', symbol: '━', preview: <GradientLinePreview />, description: 'Fading gradient rule.', color: 'text-teal-700', bg: 'bg-teal-50', border: 'border-teal-300', activeBg: 'bg-teal-100' },
  { id: 'floral_heart', label: 'Floral Heart', symbol: '❦', preview: '· ❦ ❦ ❦ ·', description: 'Open floral heart.', color: 'text-pink-700', bg: 'bg-pink-50', border: 'border-pink-300', activeBg: 'bg-pink-100' },
  { id: 'rotated_floral_heart', label: 'Rotated Floral Heart', symbol: '❧', preview: '· ❧ ❧ ❧ ·', description: 'Rotated floral heart bullet.', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-300', activeBg: 'bg-red-100' },
];

const OrnamentCard = ({ opt, isActive, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(opt.id)}
    className={`text-left p-4 rounded-xl border-2 transition-all focus:outline-none ${
      isActive ? `${opt.activeBg} ${opt.border} shadow-md` : 'bg-white border-slate-200 hover:border-slate-300'
    }`}
  >
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center space-x-2 min-w-0">
        <span className={`text-2xl leading-none flex-shrink-0 ${opt.color}`}>{opt.symbol}</span>
        <span className="font-bold text-slate-800 text-xs truncate">{opt.label}</span>
      </div>
      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-1 ${isActive ? 'border-primary bg-primary' : 'border-slate-300 bg-white'}`}>
        {isActive && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
      </div>
    </div>
    <div className={`rounded px-2 py-1.5 text-center text-sm font-serif tracking-widest mb-1.5 overflow-hidden ${opt.color} ${opt.bg}`}>
      {typeof opt.preview === 'string' ? opt.preview : opt.preview}
    </div>
    <p className="text-xs text-slate-400 leading-snug">{opt.description}</p>
  </button>
);

// ── Completion Screen ────────────────────────────────────────────────────────
const CompletionScreen = ({ allExports, pipelineState, setPipelineState, closeProject, isTrial, isAdmin }) => {
  const epub = allExports?.epub;
  const pdf = allExports?.pdf;
  const epubHtml = allExports?.epubHtml || '';
  const pdfBase64 = allExports?.pdfBase64 || null;
  const isDownloadLocked = isTrial && !isAdmin;

  const openPreview = (type) => {
    setPipelineState(prev => ({
      ...prev,
      step: 4.5,
      exportType: type,
      previewData: { type, epubHtml, pdfBase64 },
      previewConfirmed: false,
    }));
  };

  const handleGenerateAnother = () => {
    setPipelineState(prev => ({
      ...prev,
      exportResult: null,
      exportType: null,
      previewData: null,
      allExports: null,
      previewConfirmed: false,
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="max-w-3xl mx-auto py-12 text-center"
    >
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <SafeIcon icon={FiCheckCircle} className="text-4xl text-green-500" />
      </div>
      <h2 className="text-4xl font-serif text-slate-800 mb-3">Conversion Complete!</h2>
      <p className="text-slate-500 text-lg mb-10">
        Your book has been formatted and is ready to review in the interactive mock device.
      </p>

      {isDownloadLocked && (
        <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-2xl max-w-xl mx-auto text-left">
          <div className="flex items-center space-x-2 text-amber-800 font-bold text-sm mb-1">
            <SafeIcon icon={FiLock} />
            <span>Trial Account — Download Locked</span>
          </div>
          <p className="text-xs text-amber-700 leading-relaxed">
            You can fully preview both the EPUB and print PDF in the reader device. To download the final retailer-ready files, please upgrade to a Single or Lifetime pass on Skillplate.
          </p>
        </div>
      )}

      {/* Format cards */}
      <div className={`grid gap-6 mb-10 ${epub && pdf ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 max-w-sm mx-auto'}`}>
        {epub && (
          <div className="bg-white rounded-2xl border-2 border-primary/20 shadow-lg p-6 text-left">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiSmartphone} className="text-primary text-xl" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800">EPUB</h3>
                <p className="text-xs text-slate-500">For Kindle, Apple Books, Kobo & more</p>
              </div>
            </div>
            {epub.file_size_kb && (
              <p className="text-xs text-slate-400 mb-4">Size: {epub.file_size_kb}KB</p>
            )}
            <div className="flex flex-col gap-2">
              {epubHtml && (
                <button
                  onClick={() => openPreview('epub')}
                  className="flex items-center justify-center space-x-2 w-full px-4 py-2.5 rounded-xl border border-primary text-primary font-bold text-sm hover:bg-primary/5 transition-all"
                >
                  <SafeIcon icon={FiEye} />
                  <span>Preview EPUB Device</span>
                </button>
              )}
              {isDownloadLocked ? (
                <button
                  disabled
                  className="flex items-center justify-center space-x-2 w-full px-4 py-2.5 rounded-xl bg-slate-200 text-slate-400 font-bold text-sm cursor-not-allowed"
                >
                  <SafeIcon icon={FiLock} />
                  <span>Download Locked (Trial)</span>
                </button>
              ) : (
                <a
                  href={epub.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-2 w-full px-4 py-2.5 rounded-xl bg-primary text-white font-bold text-sm hover:bg-blue-800 transition-all shadow"
                >
                  <SafeIcon icon={FiDownload} />
                  <span>Download EPUB</span>
                </a>
              )}
            </div>
          </div>
        )}

        {pdf && (
          <div className="bg-white rounded-2xl border-2 border-secondary/20 shadow-lg p-6 text-left">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-secondary/10 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiPrinter} className="text-secondary text-xl" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800">Print PDF</h3>
                <p className="text-xs text-slate-500">KDP Paperback · {pdf.trim_size || '6x9'} trim · {pdf.file_size_kb}KB</p>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              {pdfBase64 && (
                <button
                  onClick={() => openPreview('pdf')}
                  className="flex items-center justify-center space-x-2 w-full px-4 py-2.5 rounded-xl border border-secondary text-secondary font-bold text-sm hover:bg-secondary/5 transition-all"
                >
                  <SafeIcon icon={FiEye} />
                  <span>Preview PDF Reader</span>
                </button>
              )}
              {isDownloadLocked ? (
                <button
                  disabled
                  className="flex items-center justify-center space-x-2 w-full px-4 py-2.5 rounded-xl bg-slate-200 text-slate-400 font-bold text-sm cursor-not-allowed"
                >
                  <SafeIcon icon={FiLock} />
                  <span>Download Locked (Trial)</span>
                </button>
              ) : (
                <a
                  href={pdf.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-2 w-full px-4 py-2.5 rounded-xl bg-secondary text-white font-bold text-sm hover:bg-orange-600 transition-all shadow"
                >
                  <SafeIcon icon={FiDownload} />
                  <span>Download PDF</span>
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <button
          onClick={closeProject}
          className="flex items-center space-x-2 px-8 py-4 rounded-xl bg-slate-800 hover:bg-slate-900 text-white font-bold text-sm shadow-lg transition-all"
        >
          <SafeIcon icon={FiHome} />
          <span>Return to Dashboard</span>
        </button>
        <button
          onClick={handleGenerateAnother}
          className="flex items-center space-x-2 px-8 py-4 rounded-xl border-2 border-slate-200 bg-white text-slate-600 hover:text-primary hover:border-primary/30 font-bold text-sm transition-all"
        >
          <SafeIcon icon={FiRefreshCw} />
          <span>Change Style &amp; Regenerate</span>
        </button>
      </div>
    </motion.div>
  );
};

// ── Main ExportStep ──────────────────────────────────────────────────────────
const ExportStep = () => {
  const { pipelineState, buildBothFormats, buildEpub, buildPdf, setPipelineState, loading, closeProject } = useFormat();
  const { currentUser } = useData();
  const [selectedStyle, setSelectedStyle] = useState(
    pipelineState?.exportConfig?.style_preset ?? 'Classic Novel'
  );
  const [selectedOrnament, setSelectedOrnament] = useState(
    pipelineState?.exportConfig?.ornament_type ?? 'star'
  );

  const accessType = (currentUser?.access_type || 'trial').toLowerCase();
  const isAdmin = accessType === 'admin';
  const isTrial = accessType === 'trial';

  const recommendedIds = STYLE_RECOMMENDATIONS[selectedStyle] ?? [];
  const recommendedOrnaments = recommendedIds.map(id => ALL_ORNAMENTS.find(o => o.id === id)).filter(Boolean);
  const otherOrnaments = ALL_ORNAMENTS.filter(o => !recommendedIds.includes(o.id));
  const activeOrnament = ALL_ORNAMENTS.find(o => o.id === selectedOrnament) ?? ALL_ORNAMENTS[0];

  const handleStyleChange = (styleId) => {
    setSelectedStyle(styleId);
    const recs = STYLE_RECOMMENDATIONS[styleId] ?? [];
    if (recs.length > 0 && !recs.includes(selectedOrnament)) {
      setSelectedOrnament(recs[0]);
    }
  };

  // Show completion screen if both exports done
  if (pipelineState?.previewConfirmed && pipelineState?.allExports) {
    return (
      <CompletionScreen
        allExports={pipelineState.allExports}
        pipelineState={pipelineState}
        setPipelineState={setPipelineState}
        closeProject={closeProject}
        isTrial={isTrial}
        isAdmin={isAdmin}
      />
    );
  }

  // Also show completion if coming back from preview
  if (pipelineState?.previewConfirmed && pipelineState?.exportResult) {
    const isSinglePdf = pipelineState.exportType === 'pdf';
    return (
      <CompletionScreen
        allExports={{
          epub: isSinglePdf ? null : pipelineState.exportResult,
          pdf: isSinglePdf ? pipelineState.exportResult : null,
          epubHtml: pipelineState.previewHtml || '',
          pdfBase64: pipelineState.previewData?.pdfBase64 || null,
        }}
        pipelineState={pipelineState}
        setPipelineState={setPipelineState}
        closeProject={closeProject}
        isTrial={isTrial}
        isAdmin={isAdmin}
      />
    );
  }

  const handleGenerateBoth = async () => {
    if (!buildBothFormats) {
      await buildEpub(selectedStyle, selectedOrnament);
      return;
    }
    await buildBothFormats(selectedStyle, selectedOrnament);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto py-8"
    >
      {/* Header */}
      <div className="text-center mb-10">
        <h2 className="text-3xl font-serif text-slate-800 mb-3">Book Layout Style</h2>
        <p className="text-slate-500">
          Choose a professional preset and scene break ornament, then generate both EPUB and PDF together.
        </p>
      </div>

      {/* Preset Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {STYLE_PRESETS.map(preset => (
          <div
            key={preset.id}
            onClick={() => handleStyleChange(preset.id)}
            className={`cursor-pointer p-6 rounded-2xl border-2 transition-all ${
              selectedStyle === preset.id
                ? 'border-primary bg-primary/5 shadow-md'
                : 'border-slate-200 hover:border-primary/30 bg-white'
            }`}
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
              selectedStyle === preset.id ? 'bg-primary text-white' : 'bg-slate-100 text-slate-500'
            }`}>
              <SafeIcon icon={preset.icon} className="text-xl" />
            </div>
            <h3 className="font-bold text-slate-800 mb-2">{preset.id}</h3>
            <p className="text-sm text-slate-500 leading-relaxed">{preset.desc}</p>
          </div>
        ))}
      </div>

      {/* Ornament Picker */}
      <motion.div
        key={selectedStyle}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="mb-8"
      >
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiStar} className="text-primary text-lg" />
            <h3 className="font-black text-slate-800 uppercase tracking-widest text-xs">Scene Break Ornament</h3>
          </div>
          <p className="text-sm text-slate-500 mb-5 leading-relaxed">
            Choose the decorative mark that replaces every{' '}
            <code className="bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded text-xs font-mono">* * *</code>{' '}
            break in your manuscript.
          </p>

          <div className="mb-4">
            <div className="flex items-center space-x-2 mb-3">
              <span className="text-[10px] font-black uppercase tracking-widest text-primary bg-primary/10 px-2 py-1 rounded-full">
                ★ Recommended for {selectedStyle}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {recommendedOrnaments.map(opt => (
                <OrnamentCard key={opt.id} opt={opt} isActive={selectedOrnament === opt.id} onSelect={setSelectedOrnament} />
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-3 my-5">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">More Options</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
            {otherOrnaments.map(opt => (
              <OrnamentCard key={opt.id} opt={opt} isActive={selectedOrnament === opt.id} onSelect={setSelectedOrnament} />
            ))}
          </div>

          <div className={`rounded-xl px-4 py-3 flex items-center space-x-3 border ${activeOrnament.bg} ${activeOrnament.border}`}>
            <span className={`text-3xl leading-none flex-shrink-0 ${activeOrnament.color}`}>{activeOrnament.symbol}</span>
            <div className="min-w-0">
              <p className={`font-bold text-sm ${activeOrnament.color}`}>Selected: {activeOrnament.label}</p>
              <p className="text-xs text-slate-500 truncate">{activeOrnament.description}</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Generate Section */}
      <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
        <h3 className="font-serif text-xl text-slate-800 mb-2 text-center">Ready to Generate</h3>
        <p className="text-slate-500 mb-6 leading-relaxed text-center">
          FormatterPro will apply the <strong>{selectedStyle}</strong> stylesheet with{' '}
          <strong>{activeOrnament.label}</strong> scene breaks and generate <strong>both EPUB and Print PDF</strong> together.
        </p>

        {isTrial && !isAdmin && (
          <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-xl text-center text-xs text-amber-800 max-w-lg mx-auto">
            You are on <strong>Trial access</strong>. You can generate and view your book in the interactive preview device. Output downloads are unlocked after purchasing on Skillplate.
          </div>
        )}

        {/* Primary: Generate Both */}
        <div className="flex justify-center mb-4">
          <button
            onClick={handleGenerateBoth}
            disabled={loading}
            className="flex items-center space-x-3 bg-gradient-to-r from-primary to-secondary hover:from-blue-800 hover:to-orange-600 text-white px-10 py-5 rounded-xl font-black uppercase tracking-widest text-sm transition-all shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}>
                  <SafeIcon icon={FiLoader} className="text-xl" />
                </motion.div>
                <span>Generating Both Formats...</span>
              </>
            ) : (
              <>
                <SafeIcon icon={FiSmartphone} className="text-xl" />
                <span>Generate EPUB + PDF</span>
                <SafeIcon icon={FiPrinter} className="text-xl" />
              </>
            )}
          </button>
        </div>

        {/* Secondary: individual buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => buildEpub(selectedStyle, selectedOrnament)}
            disabled={loading}
            className="flex items-center justify-center space-x-2 px-6 py-3 rounded-xl border-2 border-primary text-primary font-bold text-sm hover:bg-primary/5 transition-all disabled:opacity-40"
          >
            <SafeIcon icon={FiSmartphone} />
            <span>EPUB Only</span>
          </button>
          <button
            onClick={() => buildPdf(selectedStyle, selectedOrnament)}
            disabled={loading}
            className="flex items-center justify-center space-x-2 px-6 py-3 rounded-xl border-2 border-secondary text-secondary font-bold text-sm hover:bg-secondary/5 transition-all disabled:opacity-40"
          >
            <SafeIcon icon={FiPrinter} />
            <span>PDF Only</span>
          </button>
        </div>

        <p className="text-center text-xs text-slate-400 mt-4 flex items-center justify-center space-x-1">
          <SafeIcon icon={FiEye} />
          <span>You'll be able to preview each format before downloading</span>
        </p>
      </div>
    </motion.div>
  );
};

export default ExportStep;